<template>
  <div class="w-full relative" :class="classProps">
    <div class="tabs-header" :class="sticky ? stickyClass : ''">
      <div class="flex gap-2 mb-4 justify-center flex-wrap items-center" v-if="showButton">
        <template v-for="tab in tabs" :key="tab.id">
          <!--
            A tab marked `separated` gets a rule before it, so it reads as not a
            member of the set beside it. Hidden below sm, where the row wraps and
            a floating divider would land in the middle of nothing.
          -->
          <span v-if="tab.separated" class="tabs-divider hidden sm:block" aria-hidden="true"></span>

          <button
            class="font-medium transition-colors duration-200 border focus:outline-none"
            :class="
              tab.variant === 'agent'
                ? ['tabs-tab-agent', { 'tabs-tab-agent--on': activeTab === tab.id }]
                : [
                    'px-3 py-1.5 text-sm md:px-6 md:py-2.5 md:text-base rounded-lg border-line',
                    activeTab === tab.id
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-ink-mute hover:bg-surface-2',
                  ]
            "
            @click="setActiveTab(tab.id)"
          >
            <!--
              Lets a caller put something before the label — an avatar, a mark —
              without this component having to know what an agent is.
            -->
            <slot name="tab-prefix" :tab="tab" :active="activeTab === tab.id"></slot>
            <!--
              The label is wrapped so a variant can paint it independently of
              the button's own background — the agent variant runs a gradient
              through the text, which needs an element of its own to clip to.
              Unstyled for every other tab, so the dozen existing ones render
              byte-for-byte as before.
            -->
            <span class="tabs-tab-label">{{ tab.label }}</span>
            <slot name="tab-suffix" :tab="tab" :active="activeTab === tab.id"></slot>
          </button>
        </template>
      </div>
      <!--
        The actions slot can drive the tabs itself: with `showButton` off, a
        caller may render its own navigation (icons in an action row, say)
        and call `setActiveTab` — the tab switching stays in here, the caller
        only decides what the control looks like and where it sits.
      -->
      <div v-if="$slots.actions" class="tabs-actions">
        <slot name="actions" :active-tab="activeTab" :set-active-tab="setActiveTab"></slot>
      </div>
    </div>
    <div>
      <slot :name="activeTab"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineEmits, defineProps, ref } from 'vue';

interface Tab {
  id: string;
  label: string;
  /**
   * Opt-in appearance. Omitted, a tab looks exactly as it always has — the
   * dozen existing ones are untouched.
   *
   * 'agent' is the rounded, avatar-carrying treatment used by Ask ELSA
   * (AB#21202): it is not a fifth view of the order, it is someone you ask, and
   * a control shaped like its neighbours cannot say that.
   */
  variant?: 'agent';
  /** Draw a rule before this tab, separating it from the set. */
  separated?: boolean;
}

const props = defineProps({
  tabs: Array as () => Tab[],
  initialTab: {
    type: String,
    required: true,
  },
  showButton: {
    type: Boolean,
    default: true,
  },
  sticky: {
    type: Boolean,
    default: false,
  },
  stickyClass: {
    type: String,
    default: undefined,
    required: false,
  },
  classProps: {
    type: String,
    default: '',
    required: false,
  },
});

const emit = defineEmits<{
  (e: 'change', tabId: string): void;
}>();

const activeTab = ref(props.initialTab || (props.tabs.length > 0 ? props.tabs[0].id : ''));

const setActiveTab = (tabId: string) => {
  activeTab.value = tabId;
  emit('change', tabId);
};
</script>

<style scoped>
/*
 * The agent tab (AB#21202).
 *
 * Fully rounded against squared tabs, and purple rather than the brand teal:
 * teal already means "selected" in this row, and one hue cannot carry two
 * meanings and stay legible. The avatar arrives through the tab-prefix slot and
 * the freshness dot through tab-suffix, so this component never learns what
 * ELSA is.
 */
.tabs-divider {
  width: 1px;
  align-self: stretch;
  min-height: 30px;
  margin-inline: 6px;
  background: #d1d5db;
}

/*
 * A gradient hairline ring drawn without an extra wrapper element.
 *
 * Two backgrounds on one box: the flat fill is clipped to the padding box, the
 * gradient to the border box, and a transparent border lets the second show
 * through only in the ring. A `border-image` cannot do this — it does not
 * follow a border-radius — and a pseudo-element ring would sit above the label
 * and eat the clicks.
 */
.tabs-tab-agent {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 17px 4px 4px;
  border-radius: 9999px;
  font-size: 14px;
  border: 2.5px solid transparent;
  background:
    linear-gradient(#ffffff, #ffffff) padding-box,
    linear-gradient(120deg, #6d4ae0, #9a6bff 45%, #4ac8e0) border-box;
  box-shadow: 0 6px 18px -9px rgba(109, 74, 224, 0.7);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

/* The label carries the gradient too, clipped to the glyphs. */
.tabs-tab-agent .tabs-tab-label {
  font-weight: 600;
  background: linear-gradient(100deg, #4b2bb8, #7b45d6);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}

/*
 * Forced-colours and any engine without background-clip: text would render the
 * label invisible. Paint it back to a real colour there rather than leaving a
 * blank pill.
 */
@supports not ((background-clip: text) or (-webkit-background-clip: text)) {
  .tabs-tab-agent .tabs-tab-label {
    color: #4b2bb8;
  }
}

@media (forced-colors: active) {
  .tabs-tab-agent .tabs-tab-label {
    color: ButtonText;
    background: none;
  }
}

.tabs-tab-agent:hover {
  box-shadow: 0 8px 22px -8px rgba(109, 74, 224, 0.8);
  transform: translateY(-1px);
}

/* Selected: the ring floods the pill and the text turns out of it. */
.tabs-tab-agent--on {
  background:
    linear-gradient(120deg, #6d4ae0, #8a5cf0 55%, #7b52e8) padding-box,
    linear-gradient(120deg, #6d4ae0, #9a6bff 45%, #4ac8e0) border-box;
}

.tabs-tab-agent--on .tabs-tab-label {
  background: none;
  -webkit-text-fill-color: #ffffff;
  color: #ffffff;
}

.tabs-tab-agent:focus-visible {
  outline: 2px solid #5b3fb0;
  outline-offset: 3px;
}

@media (prefers-reduced-motion: reduce) {
  .tabs-tab-agent {
    transition: none;
  }

  .tabs-tab-agent:hover {
    transform: none;
  }
}

/*
 * Sized here rather than in the avatar so the tab owns its own proportions.
 * The soft disc behind it belongs to the pill, not to the avatar — the avatar
 * is drawn on transparent so this shows through.
 */
.tabs-tab-agent :deep(.elsa-avatar-slot) {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(140deg, #efe9ff, #e4f7fb);
  display: grid;
  place-items: center;
}

.tabs-tab-agent--on :deep(.elsa-avatar-slot) {
  background: rgba(255, 255, 255, 0.92);
}

/*
 * The freshness dot, when a caller supplies one through tab-suffix.
 *
 * The ring is not decoration: selected, this pill is a purple gradient, and an
 * amber dot straight onto it is two mid-tones touching. The ring gives it an
 * edge on either ground.
 */
.tabs-tab-agent :deep(.tabs-tab-dot) {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
  margin-left: -1px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95);
}
</style>
